import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Search, CornerDownLeft } from "lucide-react";
import { useI18n } from "i18n";

// Global search overlay: Ctrl/Cmd+F anywhere opens it, Enter runs the search.
// It deliberately overrides the browser's own find-in-page — that is the requested behaviour;
// Esc closes and gives the page back.
export default function SearchOverlay() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      const isFind = (e.key === "f" || e.key === "F") && (e.metaKey || e.ctrlKey);
      if (isFind) {
        e.preventDefault();
        setOpen(true);
        return;
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      // select the previous term so typing replaces it
      setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 10);
    }
  }, [open]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (term.length < 2) return;
    setOpen(false);
    navigate(`/liveness/search?q=${encodeURIComponent(term)}`);
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-[18vh] px-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <form onSubmit={submit}
        className="w-full max-w-xl rounded-2xl bg-card border border-border shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-4">
          <Search className="h-5 w-5 text-muted-foreground shrink-0" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t("search.placeholder", "Buscar em todo o sistema…")}
            className="flex-1 h-14 bg-transparent text-base outline-none placeholder:text-muted-foreground" />
          <button type="submit" disabled={q.trim().length < 2}
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40">
            <CornerDownLeft className="h-3.5 w-3.5" /> {t("search.enter", "Enter")}
          </button>
        </div>
        <div className="px-4 py-2.5 border-t border-border text-xs text-muted-foreground">
          {t("search.hint", "Nome, e-mail, CPF, telefone, empresa, app ou id de sessão. Esc para fechar.")}
        </div>
      </form>
    </div>, document.body);
}
